import React, { useMemo, useState } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useGetChannelOrderCountFUOutSideQuery, useGetChannelOrderCountFUQuery, useGetChannelOrderCountUFQuery } from "../../../redux/commonRTK";
import { subMonths } from "date-fns";
import { formatDate, formatDateForApi } from "../../../helpers/UtilityHelper";
import Loader from "../../../common/Loader";
import DateRangePicker from "../../DateRangePicker";
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER, WHITE, PRIMARY_BLUE, SUCCESS, INFO } from '../../../styles/foundation/colors';

const ChannelOrderAnalytics = ({ merchantId }) => {

    const [dateRange, setDateRange] = useState([
        {
            startDate: subMonths(new Date(), 1),
            endDate: new Date(),
            key: 'selection'
        }
    ]);

    const payloadUF = useMemo(() =>
        [
            { field: "createdDate", operator: "gte", value: formatDateForApi(dateRange[0].startDate, 'start') },
            { field: "createdDate", operator: "lte", value: formatDateForApi(dateRange[0].endDate, 'end') },
            { field: "channelOrderStatus", operator: "in", value: ['CREATED', 'CANCELLED', 'HOLD', 'DELETED', 'RELEASE', 'PARTIALLY_FULLFILLED'] },
            { field: "merchant.$id", operator: "eq", value: 'ObjectId(\'' + merchantId + '\')' }
        ], [dateRange, merchantId]);

    const payloadFU = useMemo(() =>
        [
            { field: "createdDate", operator: "gte", value: formatDateForApi(dateRange[0].startDate, 'start') },
            { field: "createdDate", operator: "lte", value: formatDateForApi(dateRange[0].endDate, 'end') },
            { field: "channelOrderStatus", operator: "eq", value: "FULFILLED" },
            { field: "merchant.$id", operator: "eq", value: 'ObjectId(\'' + merchantId + '\')' }
        ], [dateRange, merchantId]);

    const payloadFUOutSide = useMemo(() =>
        [
            { field: "createdDate", operator: "gte", value: formatDateForApi(dateRange[0].startDate, 'start') },
            { field: "createdDate", operator: "lte", value: formatDateForApi(dateRange[0].endDate, 'end') },
            { field: "channelOrderStatus", operator: "eq", value: "FULLFILLED_OUTSIDE" },
            { field: "merchant.$id", operator: "eq", value: 'ObjectId(\'' + merchantId + '\')' }
        ], [dateRange, merchantId]);


    const { data: fulfilledData, isLoading: isLoadingFulfilled } = useGetChannelOrderCountFUQuery(payloadFU);
    const { data: unFulfilleddata, isLoading: isLoadingOther } = useGetChannelOrderCountUFQuery(payloadUF);
    const { data: fulfilledOutSideData, isLoading: isLoadingFUOutSide } = useGetChannelOrderCountFUOutSideQuery(payloadFUOutSide);

    const total = fulfilledData + unFulfilleddata;
    const walletPercentage = total > 0 ? ((fulfilledData / total) * 100).toFixed(2) : 0;

    const rows = [
        { label: "Total Channel Orders", value: (fulfilledData ?? 0) + (unFulfilleddata ?? 0) + (fulfilledOutSideData ?? 0), bold: true },
        { label: "Fulfilled on Proship", value: fulfilledData ?? 0, color: SUCCESS },
        { label: "Unfulfilled", value: unFulfilleddata ?? 0, color: INFO },
        { label: "Fulfilled Outside", value: fulfilledOutSideData ?? 0, color: SUBTLE },
        { label: "Share of Wallet (%)", value: `${walletPercentage}%`, color: PRIMARY_BLUE },
    ];

    const isLoading = isLoadingFulfilled && isLoadingOther && isLoadingFUOutSide;

    return (
        <Box>
            {isLoading && <Loader />}

            <Box sx={{
                bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px',
                overflow: 'hidden',
            }}>
                {/* Header with date range */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 1.5, px: 2.5, py: 1.5,
                    borderBottom: `1px solid ${BORDER}`,
                }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: NAVY }}>
                            Channel Orders Overview
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25 }}>
                            {formatDate(dateRange[0].startDate, 'dd MMM, yyyy')} — {formatDate(dateRange[0].endDate, 'dd MMM, yyyy')}
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 220 }}>
                        <DateRangePicker
                            showTextBox
                            label="Date Range"
                            defaultRange={dateRange}
                            maxDate={new Date()}
                            onChange={(range) => setDateRange(range)}
                        />
                    </Box>
                </Box>

                {/* Summary table */}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'],
                                    textTransform: 'uppercase', color: SUBTLE, letterSpacing: '0.03em',
                                    borderBottom: `2px solid ${BORDER}`, py: 1.2, px: 2.5,
                                }}>
                                    Metric
                                </TableCell>
                                <TableCell align="right" sx={{
                                    fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'],
                                    textTransform: 'uppercase', color: SUBTLE, letterSpacing: '0.03em',
                                    borderBottom: `2px solid ${BORDER}`, py: 1.2, px: 2.5,
                                }}>
                                    Count
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, idx) => (
                                <TableRow key={idx} sx={{
                                    '&:last-child td': { borderBottom: 0 },
                                    ...(row.bold ? { bgcolor: '#f8f9fa' } : {}),
                                }}>
                                    <TableCell sx={{
                                        fontFamily: FONT, fontSize: SIZE['2xs'],
                                        fontWeight: row.bold ? WEIGHT.semibold : WEIGHT.regular,
                                        color: NAVY, py: 1.2, px: 2.5,
                                        borderBottom: `1px solid ${BORDER}`,
                                    }}>
                                        {row.label}
                                    </TableCell>
                                    <TableCell align="right" sx={{
                                        fontFamily: FONT, fontSize: SIZE.sm,
                                        fontWeight: WEIGHT.semibold,
                                        color: row.color || NAVY, py: 1.2, px: 2.5,
                                        borderBottom: `1px solid ${BORDER}`,
                                    }}>
                                        {row.value}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default ChannelOrderAnalytics;
